// core の Battle を描画用の形（VBoard）に読み替える。画面が core の内部構造に触れるのはこのファイルだけ。
import {
  CHARS,
  ENEMIES,
  surgeInfo,
  surgeLabel,
  surgeRules,
  surgeStageName,
  surgeTraitTable,
  unitSurgeStage,
  unitSurgeTraits,
  unitStatus,
  insideTerritory,
  heroItems,
  defenseInfo,
  turnLimitInfo,
  coreGuardMult,
  ougiInfo,
  castInfo,
  zonesView,
  respawnView,
  unitEnemyType,
  type BuildingType,
  type GroundKind,
  type CharId,
  type DefenseInfo,
  type ItemId,
  type EnemyRole,
  type Side,
  type StatusKey,
  type TargetRef,
  type TerritoryRule,
} from '@hyakki/core';
import type { Battle, BattleApi } from '../core-bridge';
import { BUILDING_INFO } from '../assets';

export type TileKind = 'grass' | 'block' | 'core' | 'home' | 'camp' | 'boss';
export interface VTile {
  kind: TileKind;
  owner: Side | null;
  relay: boolean;
  side: Side | null;
  pickup: 'chest' | 'shrine' | null;
  /** 土地ギミック（上の難易度だけ） */
  ground?: GroundKind;
  v: number;
}
export type UnitKind = 'hero' | 'rival' | 'neutral' | 'boss' | 'building';
export interface VUnit {
  id: number;
  kind: UnitKind;
  side: Side | 'neutral';
  cell: number | null;
  hp: number;
  max: number;
  alive: boolean;
  name: string;
  charId?: CharId;
  enemyId?: string;
  role: EnemyRole | null;
  btype?: BuildingType;
  lv?: number;
  hidden?: boolean;
  stats: { atk?: number; def?: number; agi?: number; move?: number; range?: number };
  skillName?: string;
  skillWait?: number;
  /** 賽3つ：この手番の役・賽の条件の効果（攻撃の倍率・被ダメの倍率・建築の陣材0・役） */
  luck?: { atk?: number; guard?: number; move?: number; free: boolean; role?: string };
  defending?: boolean;
  engaged?: boolean;
  respawnAt?: number | null;
  telegraph?: number[] | null;
  /** 見た目が妖のヒーロー（プロローグの酒呑童子）の、攻撃・奥義の演出に使うキャラ */
  fx?: FxLook;
  /** 妖力の段階（ヒーローだけ。1..3） */
  surge?: number;
  /** 状態異常（残り手番） */
  status?: { key: StatusKey; turns: number }[];
  /** 自陣の中にいて陣地の効果を受けている（ヒーローだけ） */
  inside?: boolean;
  /** 敵の型（重装・攻城）。重装は鎧の残り、攻城は狙う本陣 */
  etype?: { type: 'heavy' | 'siege'; name: string; desc: string; armor?: number; armorMax?: number; siege?: Side };
}

export interface SurgeTraitView {
  id: string;
  name: string;
  desc: string;
}
/** 妖力の表示用（陣営ごと）。表示名は core の surgeLabel / surgeStageName から */
/** 化けた主人公のエフェクト・奥義の絵のキー（キャラID、またはプロローグの酒呑童子） */
export type FxLook = CharId | 'shuten';

export interface SurgeView {
  label: string;
  stage: number;
  stageName: string;
  maxStage: number;
  points: number;
  nextAt: number | null;
  toNext: number | null;
  /** 次の段階までの進み具合 0..1（最大なら 1） */
  progress: number;
  /** 段階ごとの必要ポイント（累計） */
  thresholds: number[];
  /** 特性のキー（キャラID または rival_<型>） */
  key: string | null;
  /** 見た目だけ別の人物（プロローグの酒呑童子）。メダリオンの絵に使う */
  look?: string;
  /** 解放済みの特性 */
  traits: SurgeTraitView[];
  /** 次の段階で解放される特性（最大なら空） */
  nextTraits: SurgeTraitView[];
}
export interface VBoard {
  tiles: VTile[];
  units: VUnit[];
  cores: Record<Side, { cell: number; hp: number; max: number }>;
  /** 攻城：番人を倒した側の、本陣への攻撃が強まる残り回数（相手の城の結界が破れている間） */
  siege: Record<Side, number>;
  /** 敵の城の結界：番人（敵陣の城の手前に立つボス）が生きている間は張られ、本陣への攻撃が減る。倒すと破れる */
  guardian: boolean;
  homes: Partial<Record<Side, number>>;
  current: number | null;
  terrain: string;
  phase: string;
  roll: number | null;
  budget: number;
  materials: number;
  potions: number;
  /** 自分の道具（種類・残り・今使えないわけ） */
  items: { id: ItemId; name: string; desc: string; icon: string; count: number; block: string | null }[];
  territory: Record<Side, number>; // 0..1
  tier: Record<Side, number>;
  ruleName: string;
  /** 陣地ルールの説明 */
  ruleDesc: string;
  /** 陣地の段階（陣地率の閾値・効果の文・アイコン）。両陣営とも同じ */
  terrTiers: TerrTierView[];
  order: number[]; // 行動順（actor id）
  turn: number; // 自分の手番の数
  heroKOs: number;
  surge: Record<Side, SurgeView>;
  /** 防衛戦の状況（通常の戦いは null） */
  defense: DefenseInfo | null;
  /** 砦（手番の上限がある戦い）：上限と残りの手番（ほかの戦いは null） */
  turnLimit: { limit: number; left: number } | null;
  /** 自本陣の守り（鬼の棟梁：近くの建物で被ダメが下がる）の被ダメ倍率。守りが無ければ 1 */
  coreGuard: number;
  /** 自分の妖力の奥義（覚えていなければ null）。ready＝今放てる */
  ougi: { name: string; desc: string; ready: boolean; block: string | null } | null;
  /** 一撃離脱の移動中（自分の手番） */
  dashing: boolean;
  /** 自分の持っている術（自分を中心に放つもの。相手を狙う基本の術なら null）。aim＝マスを選んで放つ */
  cast: { id: string; name: string; desc: string; ougiDesc: string; kind: string; aim: boolean } | null;
  /** 術の陣（灯籠陣・狐火の陣） */
  zones: { kind: 'lantern' | 'foxfire'; side: Side; cells: number[]; turns: number }[];
  /** 倒れて復活を待つ武将（戻るマス・自分の手番であと何回） */
  respawns: { id: number; side: Side; cell: number; turns: number }[];
}

const SURGE_FALLBACK: SurgeView = { label: '妖力', stage: 1, stageName: '壱', maxStage: 3, points: 0, nextAt: null, toNext: null, progress: 0, thresholds: [0, 2, 5], key: null, traits: [], nextTraits: [] };

/** 陣営の妖力（ヒーローが居なくても段階は読める）。core が古くても落ちないように */
export function surgeView(b: Battle, side: Side): SurgeView {
  try {
    const s = surgeInfo(b, side);
    const hero = b.actors.find((a) => a.kind === 'hero' && a.side === side);
    const t = hero ? unitSurgeTraits(b, hero.id) : { key: null, traits: [] };
    const th = s.thresholds ?? surgeRules().thresholds;
    const prevAt = Math.min(s.points, th[s.stage - 1] ?? 0);
    const span = s.nextAt != null ? Math.max(1, s.nextAt - prevAt) : 1;
    const progress = s.nextAt == null ? 1 : Math.max(0, Math.min(1, 1 - (s.toNext ?? 0) / span));
    const next = t.key && s.stage < s.maxStage ? (surgeTraitTable()[t.key]?.[s.stage + 1] ?? []) : [];
    return { label: s.label, stage: s.stage, stageName: s.stageName, maxStage: s.maxStage, points: s.points, nextAt: s.nextAt, toNext: s.toNext, progress, thresholds: th, key: t.key, look: hero?.spec?.look, traits: t.traits, nextTraits: next };
  } catch {
    return { ...SURGE_FALLBACK, label: safe(() => surgeLabel(), '妖力'), stageName: safe(() => surgeStageName(1), '壱') };
  }
}
const safe = <T>(f: () => T, d: T): T => {
  try {
    return f() ?? d;
  } catch {
    return d;
  }
};
/** 「妖力 弐（あと3）」の文字（Lv とは書かない） */
export function surgeText(s: SurgeView, withNext = true) {
  return `${s.label} ${s.stageName}${withNext ? (s.toNext != null ? `（あと${s.toNext}）` : '（最大）') : ''}`;
}

const roleOf = (enemyId: string | null | undefined, tier?: string): EnemyRole =>
  (enemyId && ENEMIES[enemyId]?.role) || (tier === 'boss' ? 'boss' : tier === 'elite' ? 'elite' : tier === 'medium' ? 'medium' : 'small');

export interface TerrTierView {
  share: number;
  icon: string;
  text: string;
}
const pct = (v: number) => `${Math.round(v * 100)}%`;
/** 陣地の段階の効果を文とアイコンに（回復・陣材・移動・被ダメ・与ダメ） */
export function tierView(t: TerritoryRule['tiers'][number]): TerrTierView {
  const parts: string[] = [];
  let icon = 'flag';
  if (t.heal) (parts.push(`手番の最初にHP${pct(t.heal)}回復`), (icon = 'heart'));
  if (t.mat) (parts.push(`手番の最初に陣材+${t.mat}`), (icon = 'wall'));
  if (t.move) (parts.push(`移動+${t.move}`), (icon = 'move'));
  if (t.guard) (parts.push(`被ダメ-${pct(1 - t.guard)}`), (icon = 'shield'));
  if (t.huntMult) (parts.push(`中立・番人への与ダメ+${pct(t.huntMult - 1)}`), (icon = 'oni'));
  if (t.powerMult) (parts.push(`ヒーローへの与ダメ+${pct(t.powerMult - 1)}`), (icon = 'attack'));
  return { share: t.share, icon, text: parts.join('・') };
}

export function snapshot(api: BattleApi | null, b: Battle, terrain = 'meadow'): VBoard {
  // お供の見破り（comp:trap）があれば相手の落とし穴も見える
  const sight = (b.actors.find((h) => h.kind === 'hero' && h.side === 'blue')?.spec?.comp?.trap ?? 0) > 0;
  const units: VUnit[] = b.actors.map((a) => {
    const kind: UnitKind = a.kind === 'building' ? 'building' : a.kind === 'hero' ? (a.side === 'blue' ? 'hero' : 'rival') : a.kind === 'boss' ? 'boss' : 'neutral';
    // プロローグの酒呑童子のように、自分のヒーローに敵IDが付いていれば見た目と名前はその妖（性能・演出は spec.key のキャラ）
    const disguised = kind === 'hero' && !!a.enemyId;
    const charId = kind === 'hero' && !disguised ? (a.spec?.key as CharId | undefined) : undefined;
    const enemyId = a.enemyId ?? undefined;
    const name = charId ? (CHARS[charId]?.name ?? '主') : kind === 'building' ? (a.type && BUILDING_INFO[a.type]?.name) || '建物' : (enemyId && ENEMIES[enemyId]?.name) || '妖怪';
    return {
      id: a.id,
      kind,
      side: a.side,
      cell: a.cell,
      hp: a.hp,
      max: Math.max(1, a.hpMax),
      alive: a.alive,
      name,
      charId,
      enemyId,
      ...(disguised ? { fx: (a.spec?.look ?? a.spec?.key) as FxLook | undefined } : {}),
      role: kind === 'hero' || kind === 'building' ? null : kind === 'rival' ? 'rival' : roleOf(enemyId, a.tier),
      btype: a.type,
      // 表示のレベル：主Lv を10倍の目盛りにしたので、敵・中立も10倍で見せて比べやすく（計算には使わない）
      lv: (a.spec?.level ?? a.level) && a.side !== 'blue' ? (a.spec?.level ?? a.level)! * 10 : a.spec?.level ?? a.level,
      hidden: a.kind === 'building' && a.type === 'otoshiana' && a.side !== 'blue' && !sight,
      stats: { atk: a.atk, def: a.def, agi: a.agi, move: a.spec?.move, range: a.spec?.range ?? a.range },
      skillName: a.spec?.skill.name,
      skillWait: a.kind === 'hero' && api ? api.skillWait(b, a.id) : undefined,
      defending: a.defending,
      luck: a.rollAtk || a.rollGuard || a.freeBuild || a.rollMove ? { atk: a.rollAtk, guard: a.rollGuard, move: a.rollMove, free: !!a.freeBuild, role: a.rollRole } : undefined,
      engaged: !!a.engaged,
      respawnAt: a.respawnAt ?? null,
      // 敵の武将の構え（版3）は相手を狙う：予告は狙われた武将の今いるマスに出す（番人の大技は構えたマスのまま）
      telegraph: a.telegraph
        ? a.kind === 'hero'
          ? a.telegraph.wait.map((w) => b.actors.find((u) => u.id === w.id)).filter((u) => u?.alive && u.cell != null).map((u) => u!.cell as number)
          : a.telegraph.cells
        : null,
      surge: a.kind === 'hero' ? safe(() => unitSurgeStage(b, a.id), a.surge ?? 1) : undefined,
      status: safe(() => unitStatus(b, a.id).map((x) => ({ key: x.key, turns: x.turns })), []),
      inside: a.kind === 'hero' && a.alive && a.cell != null && (a.side === 'blue' || a.side === 'red') ? safe(() => insideTerritory(b, a.cell!, a.side as Side), false) : undefined,
      etype: safe(() => unitEnemyType(b, a.id) ?? undefined, undefined),
    };
  });
  const hero = b.actors.find((a) => a.kind === 'hero' && a.side === 'blue');
  let order: number[] = [];
  let share = { blue: 0, red: 0 };
  let tier = { blue: 0, red: 0 };
  let ruleName = '';
  let ruleDesc = '';
  let terrTiers: TerrTierView[] = [];
  if (api) {
    try {
      order = api.turnOrder(b, 8).map((x) => x.id);
      share = { blue: api.territoryShare(b, 'blue'), red: api.territoryShare(b, 'red') };
      tier = { blue: api.territoryTier(b, 'blue'), red: api.territoryTier(b, 'red') };
      ruleName = api.territoryRule(b)?.name ?? '';
      ruleDesc = api.territoryRule(b)?.desc ?? '';
      terrTiers = (api.territoryRule(b)?.tiers ?? []).map(tierView);
    } catch {
      /* 表示だけなので無視 */
    }
  }
  return {
    tiles: b.tiles.map((t) => ({ kind: t.t, owner: t.owner, relay: t.relay, side: t.side, pickup: t.pickup, ...(t.ground ? { ground: t.ground } : {}), v: t.v })),
    units,
    cores: { blue: { cell: b.cores.blue.cell, hp: b.cores.blue.hp, max: b.cores.blue.max }, red: { cell: b.cores.red.cell, hp: b.cores.red.hp, max: b.cores.red.max } },
    siege: { blue: b.siege?.blue ?? 0, red: b.siege?.red ?? 0 },
    guardian: b.actors.some((a) => a.kind === 'boss' && a.alive && a.side === 'red'),
    homes: { ...b.homes },
    current: b.current,
    terrain,
    phase: b.phase,
    roll: b.roll,
    budget: b.budget,
    materials: b.mat.blue,
    potions: hero?.potions ?? 0,
    items: safe(() => heroItems(b), []),
    territory: share,
    tier,
    ruleName,
    ruleDesc,
    terrTiers,
    order,
    turn: b.stats.playerTurns,
    heroKOs: b.stats.heroKOs,
    surge: { blue: surgeView(b, 'blue'), red: surgeView(b, 'red') },
    dashing: !!hero?.dashing && b.current === hero.id,
    defense: safe(() => defenseInfo(b), null),
    turnLimit: safe(() => turnLimitInfo(b), null),
    coreGuard: safe(() => coreGuardMult(b, 'blue'), 1),
    ougi: safe(() => ougiInfo(b), null),
    cast: safe(() => castInfo(b), null),
    zones: safe(() => zonesView(b), []),
    respawns: safe(() => respawnView(b), []),
  };
}

// ---------- 合法手 ----------
export interface Legal {
  roll: boolean;
  moves: Set<number>; // 止まれるマス
  pass: Set<number>; // 通れるマス（なぞり用）
  attacks: TargetRef[];
  skills: TargetRef[];
  /** 妖力の奥義で狙える相手（今放てるときだけ） */
  ougis: TargetRef[];
  builds: Map<BuildingType, Set<number>>;
  buildWhy: Partial<Record<BuildingType, string | null>>;
  potion: boolean;
  /** 今使える道具 */
  items: ItemId[];
  wait: boolean;
  /** 一撃離脱の移動中（移動か待機だけ） */
  dashing: boolean;
  /** 自分を中心に放つ術を放てる（cast）・奥義版を放てる（castOugi） */
  cast: boolean;
  castOugi: boolean;
}

export function legalOf(api: BattleApi, b: Battle): Legal {
  const lc = api.legalCommands(b);
  const reach = lc.move ? api.reachableCells(b) : [];
  const dashing = !!lc.dashing;
  const builds = new Map<BuildingType, Set<number>>();
  for (const [t, why] of Object.entries(lc.build) as [BuildingType, string | null][]) {
    if (why === null && !dashing) builds.set(t, new Set(api.buildableCells(b, t)));
  }
  return {
    roll: lc.roll,
    moves: new Set(reach.filter((r) => r.stop).map((r) => r.cell)),
    pass: new Set(reach.map((r) => r.cell)),
    attacks: lc.attack && !dashing ? api.attackTargets(b, false) : [],
    skills: lc.skill && !dashing ? api.attackTargets(b, true) : [],
    ougis: lc.ougi && !dashing ? api.attackTargets(b, true, true) : [],
    builds,
    buildWhy: lc.build,
    potion: lc.potion && !dashing,
    items: dashing ? [] : (lc.items ?? []),
    wait: lc.wait,
    dashing,
    cast: !!lc.cast && !dashing,
    castOugi: !!lc.castOugi && !dashing,
  };
}

export const refCell = (v: VBoard, t: TargetRef) => (t.kind === 'core' ? v.cores[t.side].cell : (v.units.find((u) => u.id === t.id)?.cell ?? -1));
export const sameRef = (a: TargetRef | null | undefined, b: TargetRef | null | undefined) =>
  !!a && !!b && a.kind === b.kind && (a.kind === 'core' ? a.side === (b as typeof a).side : a.id === (b as typeof a).id);

export const BUILD_WHY: Record<string, string> = {
  phase: '今は建てられません',
  locked: '未習得（強化の「建物図面」をLv1に）',
  done: 'この手番はもう建てました',
  limit: '建てられる数の上限です',
  mat: '陣材が足りません',
  cells: '建てられるマスがありません',
  cells_own: 'そばに自陣（自分の色）のマスがありません',
  cells_ground: 'そばの自陣マスは建てられる地面ではありません',
  cells_core: '本陣の隣には建てられません',
  cells_near: '自分の本陣から3マス以内に建てます',
  cells_inside: '自陣の内側（周りの半分以上が自陣）に建てます',
  cells_unit: 'そばの自陣マスがふさがっています',
};
